import { useMemo, useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, MoonStar, Users } from "lucide-react";
import NightTalk from "@/components/sleep/NightTalk";
import SleepHero from "@/components/sleep/SleepHero";
import Runway from "@/components/sleep/Runway";
import ClockDial from "@/components/sleep/ClockDial";
import NightLog from "@/components/sleep/NightLog";
import Mixer from "@/components/sleep/Mixer";
import NapTimer from "@/components/sleep/NapTimer";
import useProgress from "@/lib/useProgress";
import { PLATFORMS } from "@/lib/platforms";
import { ASKED, buildRunway } from "@/lib/sleepData";

const NIGHT = "#0B0F26";
const GOLD = "#EBD9A6";

const STARS = Array.from({ length: 46 }, (_, i) => {
  const r = Math.sin((i + 1) * 91.7) * 43758.5453;
  const f = r - Math.floor(r);
  const r2 = Math.sin((i + 7) * 47.3) * 24634.6345;
  return { x: f * 100, y: (r2 - Math.floor(r2)) * 74, s: 1 + (i % 3) * 0.7, d: f * 5, dur: 3 + (i % 4) * 1.4 };
});

/* ------------------------------ what one hour actually costs (entrance) */
const COSTS = [
  { k: "focus", label: "Attention held", at: [26, 42, 58, 78, 96, 100, 98] },
  { k: "mood", label: "Patience with people", at: [22, 38, 54, 74, 92, 100, 100] },
  { k: "crave", label: "Pull towards sugar", at: [100, 92, 78, 58, 38, 26, 24], invert: true },
  { k: "immune", label: "Immune response", at: [40, 52, 66, 82, 95, 100, 99] },
];
const HOURS = [5, 5.5, 6, 6.5, 7, 7.5, 8];

function OneHour() {
  const [i, setI] = useState(0);
  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(180deg, #0B0F26, #141A3E)" }} data-testid="sleep-one-hour">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-white/40">Drag it and watch</p>
            <h2 className="font-display text-[32px] sm:text-[44px] leading-[1.02] mt-2 max-w-lg text-white">What one hour is actually worth</h2>
          </div>
          <div className="text-right">
            <p className="font-display text-[54px] leading-none tabular-nums" style={{ color: GOLD }}>{HOURS[i]}</p>
            <p className="text-[11px] tracking-[0.18em] uppercase mt-1 text-white/40">hours a night</p>
          </div>
        </div>

        <div className="rounded-[2rem] p-6 sm:p-9" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
          <div className="space-y-6">
            {COSTS.map((c, n) => {
              const v = c.at[i];
              const tone = c.invert ? "#E2857A" : v > 88 ? "#7FC7A2" : v > 60 ? GOLD : "#E2857A";
              return (
                <div key={c.k} data-testid={`cost-${c.k}`}>
                  <div className="flex items-baseline justify-between">
                    <p className="text-[14px] text-white/70">{c.label}</p>
                    <p className="font-display text-[19px] tabular-nums" style={{ color: tone }}>{v}%</p>
                  </div>
                  <div className="h-2.5 rounded-full mt-2 overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
                    <motion.span className="block h-full rounded-full" animate={{ width: `${v}%`, background: tone }} transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: n * 0.04 }} />
                  </div>
                </div>
              );
            })}
          </div>
          <input type="range" min={0} max={6} value={i} onChange={(e) => setI(Number(e.target.value))} className="w-full mt-8" style={{ accentColor: GOLD }} aria-label="hours of sleep" data-testid="one-hour-slider" />
          <div className="flex justify-between mt-2">
            {HOURS.map((h, n) => (
              <button key={h} onClick={() => setI(n)} className="text-[11px] font-semibold tabular-nums" style={{ color: n === i ? GOLD : "rgba(255,255,255,0.28)" }} data-testid={`one-hour-${h}`}>{h}</button>
            ))}
          </div>
          <AnimatePresence mode="wait">
            <motion.p key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-7 text-[14px] text-white/55 max-w-2xl">
              {i <= 1 ? "Two nights like this and measured reaction time is close to a legal drink. Nobody feels it happening, which is the whole problem." : i <= 3 ? "This is where most people live and call it normal. The cost is not dramatic, it is a permanently slightly worse version of you." : i === 4 ? "The bottom of the honest range. Functional, with nothing spare for a hard week." : "Nothing heroic here. Just the amount at which the numbers stop dropping."}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function Asked() {
  const [on, setOn] = useState(0);
  return (
    <section style={{ background: "#0B0F26" }} data-testid="sleep-asked">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20">
        <p className="text-[10px] tracking-[0.24em] uppercase text-white/40">Asked most</p>
        <h2 className="font-display text-[32px] sm:text-[40px] leading-[1.04] mt-2 mb-9 text-white">The five questions that come first</h2>
        <div className="space-y-2">
          {ASKED.map((e, i) => (
            <div key={e.q} className="rounded-2xl overflow-hidden" style={{ background: on === i ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.035)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <button onClick={() => setOn(on === i ? -1 : i)} className="w-full flex items-center gap-4 px-6 py-4 text-left" data-testid={`asked-${i}`}>
                <span className="font-display text-lg tabular-nums" style={{ color: on === i ? GOLD : "rgba(255,255,255,0.28)" }}>{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display text-[18px] flex-1 text-white">{e.q}</span>
                <ChevronDown size={15} style={{ color: "rgba(255,255,255,0.35)", transform: on === i ? "rotate(180deg)" : "none", transition: "transform 0.3s" }} />
              </button>
              <AnimatePresence initial={false}>
                {on === i && (
                  <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden px-6 pb-5 pl-[70px] text-[14.5px] leading-[1.85] text-white/55">{e.a}</motion.p>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Sleep() {
  const [data, patch] = useProgress("sleep", { wake: "07:00", need: 7.5, pattern: null, done: {}, nights: [] });
  const [phase, setPhase] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -55]);
  const has = !!data.pattern;
  const stage = phase || "welcome";
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [stage]);
  const plan = useMemo(() => (has ? buildRunway(data.wake, data.need, data.pattern) : null), [has, data.wake, data.need, data.pattern]);

  const finish = (a) => { patch({ wake: a.wake, pattern: a.pattern, need: a.need, done: {} }); setPhase("runway"); };

  return (
    <main className="pt-16 dark-scope text-white" style={{ background: NIGHT }} data-testid="platform-page-sleep">
      {/* ================= the night stage */}
      <section ref={heroRef} className="relative overflow-hidden" style={{ minHeight: "calc(100svh - 4rem)" }} data-testid="sleep-stage">
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #101740 0%, #0D1234 46%, #0B0F26 100%)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(40% 34% at 82% 8%, rgba(235,217,166,0.16), transparent 70%), radial-gradient(46% 40% at 2% 78%, rgba(107,116,220,0.3), transparent 72%)" }} />
        {STARS.map((s, i) => (
          <motion.span key={i} className="absolute rounded-full" style={{ left: `${s.x}%`, top: `${s.y}%`, width: s.s, height: s.s, background: i % 5 === 0 ? GOLD : "#fff" }}
            animate={{ opacity: [0.15, 0.85, 0.15] }} transition={{ duration: s.dur, repeat: Infinity, delay: s.d, ease: "easeInOut" }} />
        ))}

        <motion.div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20 flex flex-col justify-center" style={{ minHeight: "calc(100svh - 4rem)", y: contentY }}>
          <AnimatePresence mode="wait">
            {stage === "welcome" && (
              <motion.div key="welcome" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -20, filter: "blur(8px)" }} transition={{ duration: 0.6 }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center w-full">
                <div className="lg:col-span-5">
                  <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-[10px] tracking-[0.28em] uppercase text-white/45">Sleep and energy · the one place that runs dark</motion.p>
                  <h1 className="font-display text-[40px] sm:text-[58px] lg:text-[68px] leading-[0.98] mt-5">
                    {[["Your", 0], ["night", 1], ["begins", 2], ["\n", -1], ["ninety", 3], ["minutes", 4], ["\n", -1], ["before", 5], ["the", 6], ["pillow.", 7]].map(([w, i], n) =>
                      w === "\n" ? <br key={n} /> : (
                        <motion.span key={n} initial={{ opacity: 0, y: 24, filter: "blur(8px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                          transition={{ delay: 0.18 + i * 0.085, duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
                          className="inline-block mr-[0.24em]" style={{ color: i >= 5 ? GOLD : "#fff" }}>{w}</motion.span>
                      ))}
                  </h1>
                  <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }} className="text-[16px] sm:text-[18px] mt-6 max-w-md leading-relaxed text-white/55">
                    Give me the hour you must be awake. Everything else gets measured backwards from it, tonight.
                  </motion.p>
                  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }} className="mt-8 flex flex-wrap items-center gap-4">
                    <button onClick={() => setPhase(has ? "runway" : "talk")} className="group inline-flex items-center gap-3 rounded-full px-8 py-4 text-base font-semibold transition-transform hover:-translate-y-0.5"
                      style={{ background: GOLD, color: "#171B3C", boxShadow: "0 24px 52px -20px rgba(235,217,166,0.6)" }} data-testid="sleep-begin">
                      {has ? `Tonight starts at ${plan.runwayStart}` : "Build tonight backwards"}
                      <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
                    </button>
                    <p className="text-[13px] text-white/45">Ninety seconds. Nothing worn,<br className="hidden sm:block" /> nothing measured, no account.</p>
                  </motion.div>
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.35 }} className="mt-9 flex flex-wrap gap-x-7 gap-y-3">
                    {[["90 min", "the runway, not the pillow"], ["14", "nights to a real shape"], ["0", "wearables required"]].map(([a, b]) => (
                      <div key={b}>
                        <p className="font-display text-2xl leading-none" style={{ color: GOLD }}>{a}</p>
                        <p className="text-[11px] mt-1 text-white/45">{b}</p>
                      </div>
                    ))}
                  </motion.div>
                </div>

                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.35, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                  className="lg:col-span-7" data-testid="sleep-hero-animation">
                  <SleepHero accent={GOLD} />
                </motion.div>
              </motion.div>
            )}
            {stage === "talk" && <NightTalk key="talk" onDone={finish} onLeave={() => setPhase("welcome")} />}
            {stage === "runway" && has && (
              <motion.div key="runway" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full">
                <Runway data={data} patch={patch} onEntrance={() => setPhase("welcome")} onRedo={() => { patch({ pattern: null, done: {} }); setPhase("talk"); }} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {stage !== "runway" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="absolute left-0 right-0 bottom-5 flex justify-center pointer-events-none">
            <p className="text-[10px] tracking-[0.2em] uppercase flex items-center gap-2 text-white/30">the instruments are below <ChevronDown size={12} className="floaty" /></p>
          </motion.div>
        )}
      </section>

      {stage !== "runway" && <OneHour />}

      <ClockDial wake={data.wake} onWake={(w) => patch({ wake: w })} />
      <NightLog
        nights={data.nights}
        onLog={(n) => patch((d) => ({ nights: [...(d.nights || []), n].slice(0, 14) }))}
        onClear={() => patch({ nights: [] })}
      />
      <Mixer />
      <NapTimer />

      {/* the room */}
      <section style={{ background: "linear-gradient(180deg, #0E1230, #131838)" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
          <div className="rounded-[2rem] p-8 sm:p-10 relative overflow-hidden grain" style={{ background: "linear-gradient(140deg, rgba(107,116,220,0.32), rgba(235,217,166,0.14))", border: "1px solid rgba(255,255,255,0.12)" }} data-testid="sleep-room">
            <span className="absolute -right-16 -top-16 w-56 h-56 rounded-full halo" style={{ background: "radial-gradient(circle, rgba(235,217,166,0.3), transparent 70%)" }} />
            <div className="flex items-start gap-4 relative">
              <Users size={22} className="mt-1 shrink-0" style={{ color: GOLD }} />
              <div>
                <h2 className="font-display text-2xl sm:text-3xl">A room for the hours nobody else is awake</h2>
                <p className="mt-2.5 max-w-xl text-[15px] text-white/65">Post at 03:40 and somebody is there. Aliases only, sleep specialists answering under their real names, and nothing ranked.</p>
                <p className="mt-4 text-[12px] text-white/45">Opens with the full platform. Your alias already works here.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Asked />

      <section style={{ background: "linear-gradient(180deg, #0B0F26, #0E1230)" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pb-16">
          <div className="rounded-[2rem] px-7 py-9 flex flex-col sm:flex-row sm:items-center gap-5 relative overflow-hidden" style={{ background: "linear-gradient(140deg, #232A63, #141A3E)", border: "1px solid rgba(235,217,166,0.2)" }}>
            <span className="absolute -right-16 -top-16 w-52 h-52 rounded-full halo" style={{ background: "radial-gradient(circle, rgba(235,217,166,0.3), transparent 70%)" }} />
            <p className="font-display text-xl sm:text-[26px] leading-snug flex-1 relative">
              {has ? `Tonight's runway starts at ${plan.runwayStart}. Lights out ${plan.bed}.` : "One hour in, and the rest of tonight arranges itself around it."}
            </p>
            <button onClick={() => { setPhase(has ? "runway" : "talk"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="shrink-0 relative rounded-full px-7 py-3.5 text-sm font-semibold" style={{ background: GOLD, color: "#171B3C" }} data-testid="sleep-bottom-cta">
              {has ? "Back to my runway" : "Build tonight backwards"}
            </button>
          </div>
        </div>
      </section>

      <section style={{ background: "#0A0D22", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
          <p className="kicker mb-4 text-white/40">The other doors</p>
          <div className="flex flex-wrap gap-2.5">
            {PLATFORMS.filter((x) => x.key !== "sleep").map((x) => (
              <Link key={x.key} to={x.path} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-transform hover:-translate-y-0.5"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }} data-testid={`door-${x.key}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: x.hue }} />{x.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
